import * as p from '@clack/prompts'
import { clackLogRows, eraseClackLine, eraseRows, spinner } from '@core-engine/utils/spinner.js'
import pc from 'picocolors'

import { runVercel } from './runner.js'
import type { AuthResult, VercelRunner } from './types.js'

const WHOAMI_TIMEOUT_MS = 30_000
/** Login polls a browser approval, so give it a long window. */
const LOGIN_TIMEOUT_MS = 300_000

/**
 * `vercel login` lines suppressed from the terminal: the CLI banner, the bare
 * `>` prompt marker, blank spacers, npm/npx install warnings from the npx
 * runner, and the post-sign-in deploy upsell. The device link/code line and
 * anything unexpected still pass through.
 */
const LOGIN_NOISE_PATTERNS = [
  /^vercel cli \d/i,
  /^>?$/,
  /^npm (warn|notice)/i,
  /congratulations! you are now signed in/i,
  /^to deploy something/i,
  /to deploy every commit automatically/i,
  /connect a git repository/i,
  /vercel\.link\/git/i
]

/** True when a login output line should reach the terminal. */
export function showVercelLoginLine(line: string): boolean {
  return !LOGIN_NOISE_PATTERNS.some((pattern) => pattern.test(line))
}

/**
 * Non-triggering auth check. `vercel whoami` exits 0 + prints the username when
 * authenticated; a non-zero exit means not signed in. Never launches a login.
 */
export async function checkVercelAuth(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<AuthResult> {
  const result = await runVercel(runner, ['whoami'], {
    cwd,
    mode: 'capture',
    timeoutMs: WHOAMI_TIMEOUT_MS,
    env
  })

  if (result.success) {
    const username = result.stdout.trim().split('\n').pop()?.trim()
    return { authed: true, username: username || undefined }
  }

  return { authed: false, reason: result.timedOut ? 'timeout' : 'not-authed' }
}

/**
 * Ensure the user is authenticated with Vercel.
 * - If already signed in, returns immediately.
 * - If `VERCEL_TOKEN` is set but invalid, fails without launching interactive login.
 * - Otherwise runs `vercel login`, which prints a clickable link + verification
 *   code and polls until approved, then re-verifies with `whoami`.
 *
 * Owns its spinners around the `whoami` checks (on the npx runner the first
 * check also downloads the Vercel CLI) and stops them before `vercel login`,
 * which inherits the TTY and would fight a running spinner animation. Callers
 * must not have their own spinner active when calling this.
 */
export async function ensureVercelAuth(
  runner: VercelRunner,
  cwd: string,
  options: {
    env?: NodeJS.ProcessEnv
    /**
     * Skip the "Signed in to Vercel as …" note when already authenticated.
     * Used by flows that re-check auth after an earlier flow already
     * announced the session (e.g. the Blob flow after the Neon flow); a
     * fresh login still prints its verify confirmation.
     */
    quietIfAuthed?: boolean
  }
): Promise<AuthResult> {
  const env = options.env ?? process.env

  const checkSpinner = spinner()
  checkSpinner.start(
    runner.source === 'npx' ? 'Checking Vercel, This may take a moment' : 'Checking Vercel'
  )
  const existing = await checkVercelAuth(runner, cwd, env)
  if (existing.authed) {
    if (options.quietIfAuthed) {
      checkSpinner.clear()
      return existing
    }
    checkSpinner.stop(signedInMessage(existing.username))
    return { ...existing, dismissNote: eraseClackLine }
  }

  // A token was supplied but whoami failed → token is invalid/expired. Do not
  // drop into an interactive browser login in what may be a headless run.
  if (env.VERCEL_TOKEN) {
    checkSpinner.stop('Could not verify VERCEL_TOKEN with Vercel')
    return { authed: false, reason: 'login-failed' }
  }

  checkSpinner.clear()

  const signInMessage = `Sign in to Vercel to continue ${pc.dim('(or press Ctrl-C to enter a connection string manually)')}`
  const signInRows = clackLogRows(signInMessage)
  p.log.info(signInMessage)

  // Count the login lines that reach the terminal (device link, "Waiting for
  // authentication..."), so the whole sign-in block can be erased once login
  // succeeds — the block is stale then, and only the verify spinner's
  // "Signed in to Vercel" note should remain when init moves on to its next
  // prompt. The filter runs for both stdout and stderr, so one counter covers
  // every visible row. Erasable only because nothing else prints below the
  // block while `vercel login` owns the terminal.
  let streamedRows = 0
  const login = await runVercel(runner, ['login'], {
    cwd,
    mode: 'stream',
    streamLineFilter: (line) => {
      const show = showVercelLoginLine(line)
      if (show) streamedRows += 1
      return show
    },
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'login-cancelled' }
  }

  eraseRows(signInRows + streamedRows)

  const verifySpinner = spinner()
  verifySpinner.start('Verifying Vercel sign-in')
  const after = await checkVercelAuth(runner, cwd, env)
  if (after.authed) {
    verifySpinner.stop(signedInMessage(after.username))
    return { ...after, dismissNote: eraseClackLine }
  }
  verifySpinner.stop('Could not verify your Vercel sign-in')
  return { authed: false, reason: 'login-failed' }
}

function signedInMessage(username?: string): string {
  return username ? `Signed in to Vercel as ${pc.cyan(username)}` : 'Signed in to Vercel'
}
